import { useState } from "react";
import {
  login,
  signup,
  requestPasswordResetEmail,
  getPasswordRecoveryRedirectUrl,
  describePasswordResetEmailError,
} from "./auth";

function AuthModal({ onClose, onLoginSuccess, onSignupSuccess }) {
  const [activeTab, setActiveTab] = useState("signin");
  const [forgotMode, setForgotMode] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [message, setMessage] = useState("");
  const [sendingReset, setSendingReset] = useState(false);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setMessage("");

    const { error } = await login(email, password);

    if (error) {
      setMessage(error.message);
    } else {
      setMessage("Login successful!");
      setTimeout(() => {
        onLoginSuccess?.();
      }, 500);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setMessage("");

    const { data, error } = await signup(email, password, username);

    if (error) {
      setMessage(error.message);
    } else {
      if (data?.session?.user) {
        setMessage("Signup successful!");
        setTimeout(() => {
          onSignupSuccess?.();
        }, 500);
      } else {
        setMessage("Signup successful! Please check your email.");
      }
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setMessage("");
    if (!email.trim()) {
      setMessage("Enter the email for your account.");
      return;
    }
    setSendingReset(true);
    const { error } = await requestPasswordResetEmail(email, getPasswordRecoveryRedirectUrl());
    setSendingReset(false);
    if (error) {
      setMessage(describePasswordResetEmailError(error));
      return;
    }
    setMessage("Check your email. Open the link to confirm, then choose a new password.");
  };

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal-header">
          <div className="auth-logo-icon" aria-hidden>
            🎬
          </div>
          <div className="auth-modal-header-text">
            <h2 className="auth-welcome-title">RateZone</h2>
            <p className="auth-welcome-subtitle">Sign in to rate and discuss</p>
          </div>
        </div>

        <div className="auth-tabs">
          <button
            className={`auth-tab ${activeTab === "signin" && !forgotMode ? "auth-tab-active" : ""}`}
            onClick={() => {
              setActiveTab("signin");
              setForgotMode(false);
              setMessage("");
            }}
          >
            Sign In
          </button>
          <button
            className={`auth-tab ${activeTab === "signup" ? "auth-tab-active" : ""}`}
            onClick={() => {
              setActiveTab("signup");
              setForgotMode(false);
              setMessage("");
            }}
          >
            Sign Up
          </button>
        </div>

        <div className="auth-form-container">
          {activeTab === "signin" && forgotMode ? (
            <form className="auth-form auth-modal-form" onSubmit={handleForgotPassword}>
              <p className="auth-forgot-intro">
                Enter your account email. A link will be sent so you can confirm and set a new password.
              </p>
              <div className="auth-input-group">
                <label className="auth-label">Email</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  className="auth-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <button className="auth-submit-button" type="submit" disabled={sendingReset}>
                {sendingReset ? "Sending…" : "Send confirmation email"}
              </button>
              <button
                type="button"
                className="auth-forgot-back"
                onClick={() => {
                  setForgotMode(false);
                  setMessage("");
                }}
              >
                Back to sign in
              </button>
            </form>
          ) : activeTab === "signin" ? (
            <form className="auth-form auth-modal-form" onSubmit={handleSignIn}>
              <div className="auth-input-group">
                <label className="auth-label">Email</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  className="auth-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="auth-input-group">
                <label className="auth-label">Password</label>
                <input
                  type="password"
                  placeholder=""
                  className="auth-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <button className="auth-submit-button" type="submit">
                Sign In
              </button>
              <button
                type="button"
                className="auth-forgot-button"
                onClick={() => {
                  setForgotMode(true);
                  setMessage("");
                }}
              >
                Forgot password?
              </button>
            </form>
          ) : (
            <form className="auth-form auth-modal-form" onSubmit={handleSignUp}>
              <div className="auth-input-group">
                <label className="auth-label">Username</label>
                <input
                  type="text"
                  placeholder="Choose a username"
                  className="auth-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>

              <div className="auth-input-group">
                <label className="auth-label">Email</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  className="auth-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="auth-input-group">
                <label className="auth-label">Password</label>
                <input
                  type="password"
                  placeholder="Create a strong password"
                  className="auth-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <button className="auth-submit-button" type="submit">
                Sign Up
              </button>
            </form>
          )}

          {message && (
            <p className={`auth-message ${message.includes("successful") ? "auth-message-success" : "auth-message-error"}`}>
              {message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuthModal;
